<template>
  <v-container ref="formRoot" class="patient-information-form-wrapper">
    <div v-if="preloader">
      <SubmissionPageSkeleton />
    </div>
    <div v-if="isReopening" class="mb-4">
      <v-chip :color="isReopenEligible ? 'success' : 'warning'" variant="tonal" size="large" block class="pa-4">
        <template v-if="isReopenEligible"> ✓ Ready to re-open submission </template>
        <template v-else>
          <span v-if="orderInfo.status === 'Response received'">
            ⚠ In order to re-open a submission you need to update one or more of the following sections: Patient,
            Provider or Prescription Information.
          </span>
          <span v-if="orderInfo.status === 'Payor/Medication not supported'">
            ⚠ In order to re-open a submission you need to update either of the following fields: RxBIN, RxPCN, RxGroup,
            National Drug Code or Medication.
          </span>
          <span v-if="orderInfo.status === 'Requires more information'">
            ⚠ Review the error note, update the submission (e.g. remove or replace supporting documents), then re-open
            to resubmit.
          </span>
        </template>
      </v-chip>
    </div>

    <RegeneratingAnswersAlert
      v-if="!preloader && props.trackRegeneration && regenerationBusy && !props.workspace"
      :started-on="orderInfo.answerRegenerationStartedOn"
      :checking="isCheckingRegeneratedAnswers"
      :stoppable="isRegeneratingAnswers && props.actionsVisible && !props.viewerMode && !isReadOnlyDevice"
      :stopping="stoppingRegeneration"
      @stop="stopRegeneratingAnswers" />

    <v-defaults-provider :defaults="panelDefaults">
      <v-expansion-panels
        v-if="!preloader"
        v-model="expandedSections"
        multiple
        :variant="props.workspace ? 'accordion' : 'default'"
        :class="{ 'ws-panels': props.workspace }">
        <v-expansion-panel v-if="showAppeal" id="draft-appeal-section" value="appeal" data-section="appeal">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-file-document-edit-outline" class="mr-2"></v-icon>
            Draft an appeal
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <AppealLetterSection :order="orderInfo" @appeal-sent="(e) => emit('appealSent', e)" />
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-expansion-panel
          v-if="
            orderInfo &&
            orderInfo.portalForms &&
            orderInfo.portalForms?.length &&
            orderInfo.submissionMethod === 'CoverMyMeds'
          "
          value="portal-forms">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-checkbox-marked" class="mr-2"></v-icon>
            {{ orderInfo.portalSubmissionForm }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <PortalFormSelection
              v-if="!preloader && props.actionsVisible"
              :order-info="orderInfo"
              @change-form-selection="changeFormSelection" />
            <div v-else-if="!preloader" class="text-body-2">{{ orderInfo.portalSubmissionForm || "—" }}</div>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel
          v-if="
            orderInfo &&
            orderInfo.portalSubmissionForm &&
            orderInfo.submissionMethod === 'CoverMyMeds' &&
            !orderInfo.portalForms?.length
          "
          value="portal-forms">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-checkbox-marked" class="mr-2"></v-icon>
            {{ orderInfo.portalSubmissionForm }}
          </v-expansion-panel-title>
        </v-expansion-panel>

        <v-expansion-panel value="patient-info" data-section="patient-info">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-account" class="mr-2"></v-icon>
            Patient Information
            <span v-if="props.workspace && sectionSummary.patient" class="ws-panel-summary">{{
              sectionSummary.patient
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <PatientInformationFormPreview
              v-if="!preloader"
              :order-info="orderInfo"
              :is-editable="orderFieldsEditable"
              :form-button-clicked="formButtonClicked && orderFieldsEditable"
              @update-order-info-field="updateOrderInfoField" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Provider Information Section -->
        <v-expansion-panel value="provider-info" data-section="provider-info">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-doctor" class="mr-2"></v-icon>
            Provider Information
            <span v-if="props.workspace && sectionSummary.provider" class="ws-panel-summary">{{
              sectionSummary.provider
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <ProviderInformationFormPreview
              v-if="!preloader"
              :order-info="orderInfo"
              :is-editable="orderFieldsEditable"
              :form-button-clicked="formButtonClicked && orderFieldsEditable"
              @update-order-info-field="updateOrderInfoField" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Medical Information Section -->
        <v-expansion-panel value="medical-info" data-section="medical-info">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-medication" class="mr-2"></v-icon>
            Prescription Information
            <span v-if="props.workspace && sectionSummary.medication" class="ws-panel-summary">{{
              sectionSummary.medication
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <MedicalInformationForm
              v-if="!preloader"
              :order-info="orderInfo"
              :is-editable="orderFieldsEditable"
              :form-button-clicked="formButtonClicked && orderFieldsEditable"
              :allow-change-formulary="true"
              @update-order-info-field="updateOrderInfoField"
              @add-extra-medication="addExtraMedication"
              @remove-extra-medication="removeExtraMedication"
              @remove-primary-medication="removePrimaryMedication"
              @update-extra-medication="updateExtraMedicationField"
              @change-formulary="changeFormulary" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Patient Diagnosis Information Section -->
        <v-expansion-panel value="diagnosis-info" data-section="diagnosis-info">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-account-heart" class="mr-2"></v-icon>
            Patient Diagnosis Information
            <span v-if="props.workspace && sectionSummary.diagnosis" class="ws-panel-summary">{{
              sectionSummary.diagnosis
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-row class="patient-information-container">
              <v-col cols="12">
                <v-row class="patient-information">
                  <v-col cols="12" class="header-with-select-box">
                    <DiagnosisSelectionPreviewComponent
                      :medication="orderInfo.medication"
                      :order-key="orderInfo.orderKey"
                      :primary-diagnosis-name="orderInfo.primaryDiagnosisName"
                      :primary-diagnosis-icd10-code="orderInfo.primaryDiagnosisIcd10Code"
                      :secondary-diagnosis="orderInfo.secondaryDiagnosis"
                      :is-editable="orderFieldsEditable"
                      :form-button-clicked="formButtonClicked && orderFieldsEditable"
                      :llm-prediction="orderInfo.diagnosisLlmScores"
                      @update-primary-diagnosis="updatePrimaryDiagnosis"
                      @update-other-diagnosis="updateOtherDiagnosis"
                      @add-other-diagnosis="addOtherDiagnosis" />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Patient Outcome Notification Section -->
        <v-expansion-panel
          v-if="orderInfo.submissionMethod === 'CoverMyMeds'"
          value="patient-notification"
          data-section="patient-notification">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-bell-ring" class="mr-2"></v-icon>
            Patient Outcome Notifications
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <PatientNotificationComponent
              :order-info="orderInfo"
              :patient-phone="orderInfo.patientPhone"
              :is-editable="orderFieldsEditable"
              @update-order-info-field="updateOrderInfoField" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Additional Questions Section -->
        <v-expansion-panel
          v-if="orderInfo && orderInfo.basicPlusQuestions && orderInfo.basicPlusQuestions.length > 0"
          value="additional-questions"
          data-section="additional-questions">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-help-circle" class="mr-2"></v-icon>
            {{ basicPlusQuestionsTitle }}
            <span v-if="props.workspace && sectionSummary.basicPlus" class="ws-panel-summary">{{
              sectionSummary.basicPlus
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text :eager="Boolean(props.attentionTarget)">
            <QuestionsSelectionComponent
              :order-key="orderInfo.orderKey"
              :title="basicPlusQuestionsTitle"
              :is-editable="orderFieldsEditable"
              :payer-questions="orderInfo.basicPlusQuestions"
              :checklist="orderInfo.basicPlusChecklist"
              rules-decide-visibility
              :llm-prediction="orderInfo.basicPlusChecklistLlmScores"
              :form-button-clicked="formButtonClicked && orderFieldsEditable"
              :submission-method="orderInfo.submissionMethod"
              :workspace="props.workspace"
              :attention-target="orderFieldsEditable ? props.attentionTarget : null"
              :reviewer="reviewer"
              @update-checklist="updateBasicPlusChecklistSelection"
              @has-live-validation-errors="(v) => onLiveValidationErrors(v, 'basicPlus')"
              @attention="(items) => onAttention('basicPlus', items)"
              @reviewed="(id) => emit('reviewed', id)" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Insurance Coverage Criteria Section -->
        <v-expansion-panel
          v-if="
            orderInfo &&
            orderInfo.payerQuestions &&
            orderInfo.payerQuestions.length > 0 &&
            !orderInfo.editableSubmissionForm
          "
          value="insurance-coverage"
          data-section="insurance-coverage">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-shield-check" class="mr-2"></v-icon>
            {{ payerQuestionsTitle }}
            <span v-if="props.workspace && sectionSummary.payer" class="ws-panel-summary">{{
              sectionSummary.payer
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text :eager="Boolean(props.attentionTarget)">
            <QuestionsSelectionComponent
              :order-key="orderInfo.orderKey"
              :title="payerQuestionsTitle"
              :payer-questions="orderInfo.payerQuestions"
              :portal-payer-questions-json="orderInfo.portalPayerQuestionsJson"
              :is-editable="clinicalAnswersEditable"
              :checklist="orderInfo.checklist"
              :llm-prediction="orderInfo.checklistLlmScores"
              :policy-reviews="orderInfo.checklistPolicyReviews"
              :form-button-clicked="formButtonClicked && clinicalAnswersEditable"
              :submission-method="orderInfo.submissionMethod"
              :workspace="props.workspace"
              :attention-target="props.attentionTarget"
              :reviewer="reviewer"
              @update-checklist="updateChecklistSelection"
              @declared-questions="updateDeclaredQuestions"
              @has-live-validation-errors="(v) => onLiveValidationErrors(v, 'checklist')"
              @clear-policy-review="onClearPolicyReview"
              @attention="(items) => onAttention('payer', items)"
              @reviewed="(id) => emit('reviewed', id)" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Editable PDF Section -->
        <v-expansion-panel
          v-if="orderInfo && orderInfo.editableSubmissionForm"
          value="editable-pdf"
          data-section="editable-pdf">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-file-pdf-box" class="mr-2"></v-icon>
            Submission Form
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <EditablePdfComponent
              v-if="!props.disabled || !props.filledFormPdfBlob"
              ref="editablePdf"
              :form-button-clicked="formButtonClicked && !props.disabled"
              :pdf-form="orderInfo.editableSubmissionForm"
              :disabled="props.disabled"
              :electronic-signature-enabled="electronicSignatureEnabled" />
            <FlattenedPdfComponent v-else :pdf-form="props.filledFormPdfBlob" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Supporting Documents Section -->
        <v-expansion-panel value="supporting-docs" data-section="supporting-docs">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-file-document" class="mr-2"></v-icon>
            Supporting Documents
            <span v-if="props.workspace && sectionSummary.documents" class="ws-panel-summary">{{
              sectionSummary.documents
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-row v-if="docsNotRequiredOptinEnabled">
              <v-col cols="12">
                <v-checkbox
                  id="docs-not-required-opt-in"
                  v-model="orderInfo.patientChartNotNeededOptIn"
                  :disabled="!supportingDocsEditable"
                  color="accent"
                  hide-details
                  label="This payor does not require supporting documents, skip validation"
                  @change="updateOrderInfoField('patientChartNotNeededOptIn', orderInfo.patientChartNotNeededOptIn)" />
              </v-col>
            </v-row>
            <SupportingDocumentsPreviewComponent
              :order-key="orderInfo.orderKey"
              :medical-necessity="orderInfo.medicalNecessity"
              :medical-necessity-letter="orderInfo.medicalNecessityLetter"
              :benefit-type="orderInfo.benefitType"
              :patient-chart="orderInfo.patientChart"
              :medical-necessity-template="orderInfo.medicalNecessityTemplate"
              :supplemental-docs="supplementalDocs"
              :form-button-clicked="formButtonClicked && supportingDocsEditable"
              :patient-chart-not-needed="orderInfo.patientChartNotNeededOptIn === true"
              :is-editable="supportingDocsEditable"
              :cmm-budgeted="orderInfo.submissionMethod === 'CoverMyMeds'"
              @add-uploaded-file="updateSupportingDocs"
              @delete-uploaded-file="updateSupportingDocs"
              @add-free-form-supplemental-docs="addFreeFormSupplementalDocs" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Submission Options Section: Smart Submit DOA attestation + expedited review -->
        <v-expansion-panel value="submission-options">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-tune-variant" class="mr-2"></v-icon>
            Submission Options
            <span v-if="props.workspace && sectionSummary.submissionOptions" class="ws-panel-summary">{{
              sectionSummary.submissionOptions
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <DoaAttestationComponent
              v-if="showDoaAttestation"
              v-model="doaAttested"
              :is-editable="doaAttestationEditable" />
            <ExpeditedReviewComponent
              :expedited-review-within24h="orderInfo.expeditedReviewWithin24h"
              :expedited-review-statement="orderInfo.expeditedReviewStatement"
              :is-editable="orderFieldsEditable"
              @update-order-info-field="updateOrderInfoField" />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <!-- Signature Section -->
        <v-expansion-panel id="signature-section" value="signature">
          <v-expansion-panel-title class="text-primary weight-600">
            <v-icon icon="mdi-pen" class="mr-2"></v-icon>
            Signature
            <span v-if="props.workspace && sectionSummary.signature" class="ws-panel-summary">{{
              sectionSummary.signature
            }}</span>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <SignatureInputComponent
              v-model:signature-image="userSignature"
              v-model:signature-strokes="userSignatureStrokes"
              :order-key="orderInfo.orderKey"
              :signature="orderInfo.signature"
              :form-button-clicked="formButtonClicked && !props.disabled"
              :disabled="props.disabled"
              :electronic-signature-enabled="electronicSignatureEnabled"
              @update-signature="updateSignature" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-defaults-provider>
    <v-row
      v-if="!preloader && !isReadOnlyDevice && props.actionsVisible && props.workspace && !props.viewerMode"
      class="mt-4 justify-end"
      dense>
      <v-col v-if="actionState.regenerateVisible" cols="auto">
        <v-tooltip :text="REGENERATE_ANSWERS_TOOLTIP" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              variant="outlined"
              color="primary"
              rounded="lg"
              :loading="regeneratingAnswers"
              :disabled="actionState.saving"
              @click="emit('regenerateRequested')">
              Regenerate
            </v-btn>
          </template>
        </v-tooltip>
      </v-col>
      <v-col cols="auto">
        <v-tooltip
          :text="actionState.prescriberDisabledReason || ''"
          :disabled="!actionState.prescriberDisabledReason"
          location="top">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-block">
              <v-btn
                variant="outlined"
                color="primary"
                rounded="lg"
                :loading="actionState.prescriberSending"
                :disabled="actionState.prescriberDisabled || actionState.saving"
                @click="sendToPrescriber">
                Send to prescriber
              </v-btn>
            </span>
          </template>
        </v-tooltip>
      </v-col>
      <v-col cols="auto">
        <v-tooltip :text="actionState.submitDisabledReason || `Send to plan  ${MOD_KEY} Enter`" location="top">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-block">
              <v-btn
                color="primary"
                variant="flat"
                rounded="lg"
                :loading="actionState.saving"
                :disabled="actionState.submitDisabled"
                @click="emit('sendToPlanRequested')">
                {{ actionState.submitLabel }}
              </v-btn>
            </span>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row v-if="!preloader && !isReadOnlyDevice && props.actionsVisible && !props.workspace" class="mt-4 justify-end">
      <v-col v-if="props.archivable && orderInfo?.orderKey" cols="auto">
        <v-btn v-if="isArchived" variant="outlined" @click="emit('unarchive')">Unarchive</v-btn>
        <v-tooltip v-else :text="REGENERATION_BUSY_REASON" location="top" :disabled="!regenerationBusy">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-block">
              <v-btn variant="outlined" :disabled="regenerationBusy" @click="emit('archive')">Archive</v-btn>
            </span>
          </template>
        </v-tooltip>
      </v-col>
      <v-col cols="auto">
        <v-tooltip :text="prescriberDisabledReason || ''" location="top" :disabled="!prescriberDisabledReason">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-block">
              <v-btn
                variant="outlined"
                :disabled="isSendToPrescriberDisabled"
                :loading="actionState.prescriberSending"
                @click="sendToPrescriber">
                Send to Prescriber
              </v-btn>
            </span>
          </template>
        </v-tooltip>
      </v-col>
      <v-col v-if="canRegenerateAnswers" cols="auto">
        <v-tooltip :text="REGENERATE_ANSWERS_TOOLTIP" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              variant="outlined"
              :disabled="draftSavingInProgress"
              :loading="regeneratingAnswers"
              @click="regenerateAnswers">
              Regenerate
            </v-btn>
          </template>
        </v-tooltip>
      </v-col>
      <v-col cols="auto">
        <v-btn
          variant="outlined"
          :disabled="draftSavingInProgress || props.disabled"
          :loading="draftSavingInProgress"
          @click="handleSavePartialOrderInfo">
          Save Draft
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-tooltip :text="sendToPlanDisabledReason" location="top" :disabled="!isSendToPlanDisabledByDelegateAgreement">
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps" class="d-inline-block">
              <v-btn :disabled="submitButtonDisabled" @click="submitOrderInfo">
                {{ buttonText }}
              </v-btn>
            </span>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>
    <span v-if="errors.length > 0" class="input-error-notification">
      <span v-for="error in errors" :key="error">{{ error }} </span>
    </span>

    <!-- Delegate agreement fuzzy match confirmation -->
    <v-dialog v-model="showDelegateAgreementDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center text-wrap">
          <v-icon color="warning" class="mr-2">mdi-account-check</v-icon>
          Confirm provider delegate agreement
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-2">
            No exact delegate agreement was found for
            <strong>{{ enteredProviderDisplayName }}</strong> (NPI {{ orderInfo?.providerNpiNumber }}).
          </p>
          <p class="text-body-1 mb-2">
            We found a delegate agreement for
            <strong>{{ suggestedProviderDisplayName }}</strong> with the same NPI. Is this the same provider?
          </p>
          <p class="text-body-2 text-medium-emphasis">
            If you confirm, the provider name on this order will be updated to match the agreement before submit.
          </p>
        </v-card-text>
        <v-card-actions class="flex-wrap justify-end">
          <v-btn variant="outlined" @click="showDelegateAgreementDialog = false">Cancel</v-btn>
          <v-btn v-if="isSmartSubmit" variant="outlined" @click="smartSubmitWithoutDelegateAgreement">
            Smart Submit
          </v-btn>
          <v-btn color="primary" @click="confirmDelegateAgreementMatch">Yes, use agreement name</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <CmmPopupAnswersDialog
      v-model="showCmmPopupDialog"
      :questions="cmmPopupQuestionsFromConfig"
      :initial-answers="cmmPopupInitialAnswers"
      @confirm="onCmmPopupConfirm" />

    <ConfirmDialog
      v-model="showStartAutocompleteDialog"
      title="Start autocomplete?"
      :message="startAutocompleteMessage"
      confirm-text="Save & Start Autocomplete"
      cancel-text="Save Only"
      :loading="draftSavingInProgress"
      persistent
      @confirm="onStartAutocompleteConfirm"
      @cancel="onStartAutocompleteCancel" />

    <ConfirmDialog
      v-model="showProcessingFirstDialog"
      title="Clinical questions not ready"
      message="Looks like clinical questions aren't back yet for the prescriber to review. Click 'Send to Processing' to autofill the clinical questions."
      confirm-text="Send to Processing"
      cancel-text="Send anyway"
      :loading="draftSavingInProgress"
      @confirm="onProcessingFirstConfirm"
      @cancel="proceedSendToPrescriber" />
  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick, watch } from "vue";
import { storeToRefs } from "pinia";
import { MOD_KEY } from "@/utils/platformKeys";
import { useRouter, useRoute } from "vue-router";
import { listReturnTarget } from "@/composables/useListReturn";
import { mainServices } from "@/services/mainServices";
import { orderInfoService } from "@/services/orderInfoService";
import authService from "@/services/authService";
import { tryParseApiErrors, clearOrderInfoStorage, convertDateFormat } from "@/utils";
import { isValidCmmPortalSubmissionKey } from "@/utils/validators";
import { hasAnswerFormatError } from "@/utils/answerFormat";
import { basicPlusShownRules } from "@/utils/basicPlusVisibility";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useAuthStore } from "@/stores";
import { useFieldChangeTracker } from "@/composables/useFieldChangeTracker";
import {
  CMM_REOPENABLE_STATUSES,
  CMM_SUBMISSION_TYPE,
  PA_STATUS_VALUE_BY_KEY,
  REGENERATE_ANSWERS_PIPELINE_STEP,
  REGENERATE_ANSWERS_TOOLTIP,
  REGENERATION_BUSY_REASON,
  REQUIRES_REVIEW_PIPELINE_STEP,
  regenerationChecking,
  regenerationRunning,
} from "@/models/paStatus";
import { useOrderInfoUpdates } from "@/composables/useOrderInfoUpdates";
import { useResponsive } from "@/composables/useResponsive";

const { isReadOnlyDevice } = useResponsive();
import PatientInformationFormPreview from "@/pages/PatientInformationFormPreview.vue";
import ProviderInformationFormPreview from "@/pages/ProviderInformationFormPreview.vue";
import MedicalInformationForm from "@/pages/MedicalInformationForm.vue";
import QuestionsSelectionComponent from "@/components/QuestionsSelectionComponent.vue";
import DiagnosisSelectionPreviewComponent from "@/components/DiagnosisSelectionPreviewComponent.vue";
import ExpeditedReviewComponent from "@/components/ExpeditedReviewComponent.vue";
import DoaAttestationComponent from "@/components/DoaAttestationComponent.vue";
import PatientNotificationComponent from "@/components/PatientNotificationComponent.vue";
import SignatureInputComponent from "@/components/SignatureInputComponent.vue";
import SupportingDocumentsPreviewComponent from "@/components/SupportingDocumentsPreviewComponent.vue";
import EditablePdfComponent from "@/components/EditablePdfComponent.vue";
import PortalFormSelection from "@/components/PortalFormSelection.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import FlattenedPdfComponent from "@/components/FlattenedPdfComponent.vue";
import CmmPopupAnswersDialog from "@/components/CmmPopupAnswersDialog.vue";
import AppealLetterSection from "@/components/AppealLetterSection.vue";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog.vue";
import RegeneratingAnswersAlert from "@/components/RegeneratingAnswersAlert.vue";
// import PharmacyLookupComponent from "@/components/PharmacyLookupComponent";

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  filledFormPdfBlob: {
    type: Blob,
    default: null,
  },
  archivable: {
    type: Boolean,
    default: false,
  },
  // Provider read-only view (LAM-5263): render the fields, hide every action button.
  actionsVisible: {
    type: Boolean,
    default: true,
  },
  // PA workspace (LAM-5771): actions live in the bottom bar, with Send repeated under the signature.
  workspace: {
    type: Boolean,
    default: false,
  },
  viewerMode: {
    type: Boolean,
    default: false,
  },
  // Set only by pages that poll the order during a regeneration: elsewhere its banner would never clear (LAM-5655).
  trackRegeneration: {
    type: Boolean,
    default: false,
  },
  prescriberOpening: {
    type: Boolean,
    default: false,
  },
  attentionTarget: {
    type: String,
    default: null,
  },
  navigateAfterSubmit: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "sendToPrescriber",
  "sendToPlan",
  "sendToPlanRequested",
  "regenerateRequested",
  "draftSaved",
  "appealSent",
  "archive",
  "unarchive",
  "submitted",
  "attention",
  "reviewed",
  "answersRegenerationChanged",
]);

const reviewer = computed(() => {
  const user = useAuthStore().userInfo;
  if (!user) return null;
  return { id: user.pk, name: [user.firstName, user.lastName].filter(Boolean).join(" ") || null };
});
const attentionBySource = ref({ payer: [], basicPlus: [] });

function onAttention(source, items) {
  attentionBySource.value[source] = items.map((item) => ({ ...item, source }));
  emitAttention();
}

function emitAttention() {
  const basicPlus = orderFieldsEditable.value ? attentionBySource.value.basicPlus : [];
  emit("attention", [...attentionBySource.value.payer, ...basicPlus]);
}

const { orderInfo } = storeToRefs(useOrderInfoStore());

const panelDefaults = computed(() =>
  props.workspace ? { VExpansionPanelTitle: { expandIcon: "mdi-chevron-down", collapseIcon: "mdi-chevron-down" } } : {}
);

function joinParts(...parts) {
  return parts.filter(Boolean).join(" · ");
}

function answeredLabel(list) {
  const count = (list || []).length;
  return count ? `${count} answered` : "Not answered";
}

const sectionSummary = computed(() => {
  const order = orderInfo.value || {};
  const patient = [order.patientFirstName, order.patientLastName].filter(Boolean).join(" ");
  const prescriber = [order.providerFirstName, order.providerLastName].filter(Boolean).join(" ");
  const documents = supportingDocsCount.value;
  return {
    patient: joinParts(
      patient,
      order.patientDob && `DOB ${convertDateFormat(order.patientDob)}`,
      order.patientMemberId
    ),
    provider: joinParts(prescriber && `Dr. ${prescriber}`, order.providerNpiNumber && `NPI ${order.providerNpiNumber}`),
    medication: joinParts(order.medication, order.quantityPer30Days && `${order.quantityPer30Days} per 30 days`),
    diagnosis: [order.primaryDiagnosisIcd10Code, order.primaryDiagnosisName].filter(Boolean).join(" "),
    basicPlus: answeredLabel(order.basicPlusChecklist),
    payer: answeredLabel(order.checklist),
    documents: documents ? `${documents} attached` : "None attached",
    submissionOptions: joinParts(
      showDoaAttestation.value && (doaAttested.value ? "DOA" : "No DOA"),
      order.expeditedReviewWithin24h ? "Expedited" : "Not expedited"
    ),
    signature: order.signature ? "Signed" : "Not signed",
  };
});

const isArchived = computed(() => orderInfo.value?.status === "Archived");

// CMM popup: priorAuth from API (under uiConfig, camelCased by API).
const priorAuthFromConfig = computed(() => clientConfig.value?.uiConfig?.priorAuth);
const cmmPopupQuestionsFromConfig = computed(() => {
  const list = priorAuthFromConfig.value?.cmmPopupQuestions;
  return list && Array.isArray(list) ? list : [];
});
const cmmPopupAnswersFromConfig = computed(() => {
  const a = priorAuthFromConfig.value?.cmmPopupAnswers;
  return a && typeof a === "object" ? a : {};
});
// cmm_popup_confirm_required: pre-selected answers only; the pop-up still opens and the user's Continue click
// is the attestation.
const cmmPopupPrefillAnswersFromConfig = computed(() => {
  const a = priorAuthFromConfig.value?.cmmPopupPrefillAnswers;
  return a && typeof a === "object" ? a : {};
});
const cmmPopupAnswersFromSubmission = computed(() => {
  const a = orderInfo.value?.orderSubmission?.cmmPopupAnswers;
  return a && typeof a === "object" ? a : {};
});

const incompleteStatuses = ["Created", "Incomplete", "Review demographics info", "Requires review"];
const reviewStatuses = ["Review clinical info", "Requires review"];
const reopeningStatuses = CMM_REOPENABLE_STATUSES;
const isReopening = computed(() => {
  return reopeningStatuses.includes(orderInfo.value.status) && orderInfo.value.submissionMethod === "CoverMyMeds";
});
// One computed per editability rule, passed to both `is-editable` and `form-button-clicked` so a
// section can never ask for input it doesn't render (LAM-4993) — a CMM import lands here at status
// "Created" ("Fetching questions") with the whole form read-only, yet validation painted anyway
// because formButtonClicked starts true.
const orderFieldsEditable = computed(
  () => !props.disabled && (incompleteStatuses.includes(orderInfo.value.status) || isReopening.value)
);
const clinicalAnswersEditable = computed(
  () => !props.disabled && (reviewStatuses.includes(orderInfo.value.status) || isReopening.value)
);
const supportingDocsEditable = computed(() => orderFieldsEditable.value || clinicalAnswersEditable.value);
watch(orderFieldsEditable, emitAttention);
const delegateAccountSubmitRequired = computed(() => priorAuthFromConfig.value?.delegateAccountSubmitRequired === true);
// Smart Submit (LAM-5919): no delegate agreement on file, so the final submit goes out from the tenant's own CMM account.
const smartSubmitEnabled = computed(() => priorAuthFromConfig.value?.smartSubmitEnabled === true);
const isSmartSubmit = computed(
  () =>
    delegateAccountSubmitRequired.value &&
    smartSubmitEnabled.value &&
    orderInfo.value?.submissionMethod === "CoverMyMeds" &&
    !isReopening.value &&
    reviewStatuses.includes(orderInfo.value.status) &&
    orderInfo.value?.providerDelegateAgreement !== true
);
// Shown wherever Smart Submit can apply, and kept (read-only) after submit so the attestation the order went out on stays
// visible, even once a delegate agreement is added for the prescriber later.
const showDoaAttestation = computed(
  () =>
    delegateAccountSubmitRequired.value &&
    smartSubmitEnabled.value &&
    orderInfo.value?.submissionMethod === "CoverMyMeds" &&
    (orderInfo.value?.providerDelegateAgreement !== true || orderInfo.value?.prescriberDoaAttested === true)
);
// Read-only once it is history: with an agreement on file the delegate account submits and ignores it, and Re-Open
// resubmits straight away with q2 read from it, so a change there would go out without passing review again.
const doaAttestationEditable = computed(
  () =>
    orderInfo.value?.providerDelegateAgreement !== true &&
    !isReopening.value &&
    (orderFieldsEditable.value || (isSmartSubmit.value && clinicalAnswersEditable.value))
);
// CMM pop-up q2 ("same organization or health system as the prescriber") is the one the DOA attestation answers.
const DOA_POPUP_QUESTION_ID = "q2";
// OrderInfo.prescriber_doa_attested: set at Create Submission / import, saved by every save, so a draft keeps it.
const doaAttested = computed({
  get: () => orderInfo.value?.prescriberDoaAttested === true,
  set: (value) => {
    orderInfo.value.prescriberDoaAttested = value;
  },
});
function withDoaAttestation(answers) {
  if (!isSmartSubmit.value) return answers;
  return { ...answers, [DOA_POPUP_QUESTION_ID]: doaAttested.value ? "Yes" : "No" };
}
const docsNotRequiredOptinEnabled = computed(() => priorAuthFromConfig.value?.docsNotRequiredOptinEnabled === true);
const buttonText = computed(() => {
  if (isReopening.value) return "Re-Open Submission";

  if (incompleteStatuses.includes(orderInfo.value.status) && orderInfo.value.status !== "Requires review") {
    return "Send to Processing";
  } else if (isSmartSubmit.value) {
    return "Smart Submit";
  } else if (delegateAccountSubmitRequired.value && orderInfo.value.status === "Review clinical info") {
    return "Send to Plan from Delegate Account";
  } else {
    return "Send to Plan";
  }
});
const sendToPlanDisabledReason = "Provider does not have a delegate agreement. Use Send to Prescriber instead.";
const sendToPrescriberDisabledReason =
  "Cannot send to prescriber: CoverMyMeds submission key is missing or invalid. Please reimport the submission with a valid key.";
const isSendToPlanFromDelegateAction = computed(() => buttonText.value === "Send to Plan from Delegate Account");
const isSendToPlanDisabledByDelegateAgreement = computed(() => {
  if (!isSendToPlanFromDelegateAction.value || !delegateAccountSubmitRequired.value) {
    return false;
  }
  if (orderInfo.value?.providerDelegateAgreement === true) {
    return false;
  }
  if (orderInfo.value?.providerDelegateAgreementSuggestion) {
    return false;
  }
  return true;
});
const isSendToPrescriberDisabledByCmmKey = computed(() => {
  if (orderInfo.value?.submissionMethod !== "CoverMyMeds") {
    return false;
  }
  return !isValidCmmPortalSubmissionKey(orderInfo.value?.orderSubmission?.portalSubmissionId);
});

const { trackChange, isReopenEligible, requiresNewSubmission } = useFieldChangeTracker(orderInfo.value);

const {
  supplementalDocs,
  formButtonClicked,
  declaredPayerQuestionIds,
  updateChecklistSelection,
  updateDeclaredQuestions,
  updateBasicPlusChecklistSelection,
  updateSignature,
  updatePrimaryDiagnosis,
  updateOtherDiagnosis,
  addOtherDiagnosis,
  addFreeFormSupplementalDocs,
  updateSupportingDocs,
  addExtraMedication,
  removeExtraMedication,
  removePrimaryMedication,
  updateExtraMedicationField,
  validPatientInfo,
  validProviderInfo,
  validMedicationInfo,
  validDiagnosisInfo,
  validSupportingDocs,
  mergeSupplementalDocsWithPatientChart,
  dropUnansweredChecklistRows,
  savePartialOrderInfo,
} = useOrderInfoUpdates(orderInfo);

const formRoot = ref(null);
const expandedSections = ref([]);
const showDelegateAgreementDialog = ref(false);
const pendingDelegateAgreementSuggestion = ref(null);
const showCmmPopupDialog = ref(false);
const clientConfig = ref(null);
let clientConfigLoaded = Promise.resolve();

const draftSavingInProgress = ref(false);
const regeneratingAnswers = ref(false);
const stoppingRegeneration = ref(false);
const isRegeneratingAnswers = computed(() => regenerationRunning(orderInfo.value));
const isCheckingRegeneratedAnswers = computed(() => regenerationChecking(orderInfo.value));
// The API refuses saves, sends and archiving (409) until the run and its policy check are done.
const regenerationBusy = computed(() => isRegeneratingAnswers.value || isCheckingRegeneratedAnswers.value);
// Only where the page follows the run (polls and reloads); elsewhere the order would sit in Processing until a reload.
const canRegenerateAnswers = computed(
  () =>
    props.trackRegeneration &&
    !props.disabled &&
    Boolean(orderInfo.value?.orderKey) &&
    (orderInfo.value?.payerQuestions?.length ?? 0) > 0 &&
    orderInfo.value?.status === PA_STATUS_VALUE_BY_KEY.CLINICAL_REVIEW &&
    orderInfo.value?.orderSubmission?.pipelineStep === REQUIRES_REVIEW_PIPELINE_STEP &&
    orderInfo.value?.submissionMethod === CMM_SUBMISSION_TYPE
);
// Not tied to props.disabled — send-to-prescriber allowed across statuses (LAM-3090 resend).
const isSendToPrescriberDisabled = computed(
  () => draftSavingInProgress.value || isSendToPrescriberDisabledByCmmKey.value || regenerationBusy.value
);
const prescriberDisabledReason = computed(() => {
  if (isSendToPrescriberDisabledByCmmKey.value) return sendToPrescriberDisabledReason;
  if (regenerationBusy.value) return REGENERATION_BUSY_REASON;
  return null;
});
const checklistLiveErrors = ref({});
const basicPlusLiveErrors = ref({});

function onLiveValidationErrors(errorsMap, section) {
  if (section === "checklist") {
    checklistLiveErrors.value = errorsMap;
  } else {
    basicPlusLiveErrors.value = errorsMap;
  }
  // Persist cleared errors into orderInfo so the next saveOrder call writes null to the DB.
  // Without this, checklistLlmScores.validationError survives a page reload even after the
  // user has corrected the value, because _set_fields blindly re-saves whatever the frontend sent.
  const scores =
    section === "checklist" ? orderInfo.value.checklistLlmScores : orderInfo.value.basicPlusChecklistLlmScores;
  if (!scores) return;
  Object.entries(errorsMap).forEach(([answerId, error]) => {
    if (error === null) {
      const score = scores.find((s) => s.answerId === answerId);
      if (score) score.validationError = null;
    }
  });
}
// Checks the current answers, not the AI's stored validationError, and only in sections the user can
// still edit — basic+ is read-only (and shows no errors) once demographics are submitted to plan.
// Empty answers never fail here; required fields are enforced on plan submit.
const checklistHasValidationErrors = computed(() => {
  const sections = [];
  if (orderFieldsEditable.value) {
    // Only the basic+ questions the page shows (same rule, panel open or not): a hidden one isn't sent
    // and can't be fixed by the user.
    const questions = orderInfo.value.basicPlusQuestions || [];
    const checklist = orderInfo.value.basicPlusChecklist || [];
    const shown = basicPlusShownRules(questions, checklist);
    sections.push([
      questions,
      checklist.filter((answer) => shown.has(answer.id)),
      Object.fromEntries(Object.entries(basicPlusLiveErrors.value).filter(([id]) => shown.has(id))),
    ]);
  }
  if (clinicalAnswersEditable.value) {
    sections.push([orderInfo.value.payerQuestions, orderInfo.value.checklist, checklistLiveErrors.value]);
  }
  return sections.some(
    ([questions, checklist, liveErrors]) =>
      hasAnswerFormatError(questions || [], checklist || []) || Object.values(liveErrors).some(Boolean)
  );
});
const submitButtonDisabled = computed(() => {
  return (
    draftSavingInProgress.value ||
    props.disabled ||
    (isReopening.value && !isReopenEligible.value) ||
    isSendToPlanDisabledByDelegateAgreement.value
  );
});

function onClearPolicyReview(questionId) {
  const review = (orderInfo.value.checklistPolicyReviews || []).find((r) => r.questionId === questionId);
  if (review && review.alignsWithPolicy === "NO") {
    review.alignsWithPolicy = "YES";
    delete review.reason;
    delete review.policyCitation;
    delete review.policyDocumentUrl;
  }
}

function formatProviderDisplayName(firstName, lastName) {
  const parts = [firstName, lastName].filter(Boolean);
  if (!parts.length) {
    return "Unknown provider";
  }
  return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ");
}

const enteredProviderDisplayName = computed(() =>
  formatProviderDisplayName(orderInfo.value?.providerFirstName, orderInfo.value?.providerLastName)
);

const suggestedProviderDisplayName = computed(() => {
  const suggestion = pendingDelegateAgreementSuggestion.value || orderInfo.value?.providerDelegateAgreementSuggestion;
  if (!suggestion) {
    return "";
  }
  return formatProviderDisplayName(suggestion.providerFirstName, suggestion.providerLastName);
});

async function resolveDelegateAgreementCheck() {
  const providerFields = {
    providerFirstName: orderInfo.value?.providerFirstName,
    providerLastName: orderInfo.value?.providerLastName,
    providerNpiNumber: orderInfo.value?.providerNpiNumber,
  };
  if (orderInfo.value?.orderKey) {
    return orderInfoService.checkProviderDelegateAgreement(orderInfo.value.orderKey, providerFields);
  }
  return {
    hasAgreement: orderInfo.value?.providerDelegateAgreement === true,
    suggestion: orderInfo.value?.providerDelegateAgreementSuggestion || null,
  };
}

async function maybePromptDelegateAgreementConfirmation() {
  const smartSubmitApplies =
    delegateAccountSubmitRequired.value &&
    smartSubmitEnabled.value &&
    orderInfo.value?.submissionMethod === "CoverMyMeds" &&
    !isReopening.value &&
    reviewStatuses.includes(orderInfo.value.status);
  if (!smartSubmitApplies && (!isSendToPlanFromDelegateAction.value || !delegateAccountSubmitRequired.value)) {
    return false;
  }
  try {
    // Both ways: an agreement deactivated since the page loaded must flip the button off the delegate account.
    const check = await resolveDelegateAgreementCheck();
    orderInfo.value.providerDelegateAgreement = check.hasAgreement === true;
    orderInfo.value.providerDelegateAgreementSuggestion = check.hasAgreement ? null : check.suggestion || null;
    if (check.hasAgreement) {
      return false;
    }
    if (check.suggestion) {
      pendingDelegateAgreementSuggestion.value = check.suggestion;
      showDelegateAgreementDialog.value = true;
      return true;
    }
    return isSendToPlanDisabledByDelegateAgreement.value;
  } catch (err) {
    errors.value = tryParseApiErrors(err);
    return false;
  }
}

// Either representation changing is a change worth saving. Comparing the geometry too matters for
// the case that motivates this whole feature: a user whose stored signature is a legacy PNG re-draws
// it, and the new image can differ only slightly while the geometry goes from absent to present —
// which is the difference between a fax the payor can read and one they can't.
function signatureChanged() {
  if (userSignature.value !== initialUserSignature.value) {
    return true;
  }
  const current = JSON.stringify(userSignatureStrokes.value ?? null);
  const initial = JSON.stringify(initialUserSignatureStrokes.value ?? null);
  return current !== initial;
}

/**
 * Write the drawn signature to the user's profile if it changed. Returns false if the save failed.
 *
 * The pad edits refs; only this persists them, and the fax overlay reads `User.signature` /
 * `signature_strokes` back from the DB at send time — so anything that renders or sends a fax has
 * to have called this first, or it stamps the previous signature. That is why it runs from the
 * draft save as well as from submit: when a fax is refused for a signature that can't survive
 * transmission, the user re-draws in the pad and goes straight back to Preview or Send, neither of
 * which submits the order. Without this the re-draw would never leave the browser and the refusal
 * would repeat forever.
 *
 * Deliberately not gated on `userSignature.value` being truthy — clicking Clear sets it to null,
 * and that has to persist too, or a signature the user explicitly removed stays live on their
 * profile and goes out on the next fax anyway.
 */
async function persistSignatureIfChanged() {
  if (!electronicSignatureEnabled.value || !signatureChanged()) {
    return true;
  }
  try {
    await authService.updateSignature(userSignature.value, userSignatureStrokes.value);
    // Only now is the saved state the new one — otherwise every later draft save re-sends an
    // unchanged signature, and a failed save would be forgotten rather than retried.
    initialUserSignature.value = userSignature.value;
    initialUserSignatureStrokes.value = userSignatureStrokes.value;
    return true;
  } catch (err) {
    errors.value = tryParseApiErrors(err);
    return false;
  }
}

async function continueSubmitAfterDelegateChecks() {
  const isValid = await validateForm();
  if (!isValid) {
    return;
  }
  // Must happen here, not inside performSubmission() — for eFax orders (the only submission
  // method this feature applies to) this function returns right after emitting "sendToPlan"
  // below, and performSubmission() is never called at all.
  // Must not swallow a save failure: a stale or missing signature would be attached to the fax.
  if (!(await persistSignatureIfChanged())) {
    return;
  }
  emit("sendToPlan");
  if (orderInfo.value?.submissionMethod === "eFax") {
    return;
  }
  await clientConfigLoaded;
  if (needCmmPopupAnswersFromUser.value) {
    if (!cmmPopupQuestionsFromConfig.value.length) {
      errors.value = ["Couldn't load your Send to Plan settings. Refresh the page and try again."];
      return;
    }
    showCmmPopupDialog.value = true;
    return;
  }
  await performSubmission();
}

async function smartSubmitWithoutDelegateAgreement() {
  pendingDelegateAgreementSuggestion.value = null;
  showDelegateAgreementDialog.value = false;
  await continueSubmitAfterDelegateChecks();
}

async function confirmDelegateAgreementMatch() {
  const suggestion = pendingDelegateAgreementSuggestion.value;
  if (!suggestion) {
    showDelegateAgreementDialog.value = false;
    return;
  }
  orderInfo.value.providerFirstName = suggestion.providerFirstName;
  orderInfo.value.providerLastName = suggestion.providerLastName;
  orderInfo.value.providerNpiNumber = suggestion.providerNpiNumber;
  trackChange("providerFirstName", suggestion.providerFirstName);
  trackChange("providerLastName", suggestion.providerLastName);
  trackChange("providerNpiNumber", suggestion.providerNpiNumber);
  orderInfo.value.providerDelegateAgreement = true;
  orderInfo.value.providerDelegateAgreementSuggestion = null;
  pendingDelegateAgreementSuggestion.value = null;
  showDelegateAgreementDialog.value = false;
  await continueSubmitAfterDelegateChecks();
}

const hasConfigCmmPopupAnswers = computed(() => Object.keys(cmmPopupAnswersFromConfig.value).length > 0);
const hasSubmissionCmmPopupAnswers = computed(() => {
  const answers = cmmPopupAnswersFromSubmission.value;
  if (!(delegateAccountSubmitRequired.value && smartSubmitEnabled.value)) return Object.keys(answers).length > 0;
  // A Smart Submit never stores q2 (the backend answers it from the attestation), so a later delegate submit asks it.
  return cmmPopupQuestionsFromConfig.value.every(
    (q) => answers[q.id] || (isSmartSubmit.value && q.id === DOA_POPUP_QUESTION_ID)
  );
});
const needCmmPopupAnswersFromUser = computed(() => {
  if (orderInfo.value?.submissionMethod !== "CoverMyMeds") return false;
  if (!reviewStatuses.includes(orderInfo.value?.status)) return false;
  if (hasSubmissionCmmPopupAnswers.value) return false;
  if (hasConfigCmmPopupAnswers.value) return false;
  return true;
});
const cmmPopupInitialAnswers = computed(() => {
  const submission = cmmPopupAnswersFromSubmission.value;
  const config = cmmPopupAnswersFromConfig.value;
  const prefill = cmmPopupPrefillAnswersFromConfig.value;
  return withDoaAttestation({ ...prefill, ...config, ...submission });
});

const errors = ref([]);

const router = useRouter();
const preloader = ref(false);
const editablePdf = ref(null);
const payerQuestionsTitle = "Insurance Coverage Criteria";
const basicPlusQuestionsTitle = "Additional Questions";

const appealLettersEnabled = computed(() => priorAuthFromConfig.value?.appealLettersEnabled ?? false);
const electronicSignatureEnabled = computed(() => priorAuthFromConfig.value?.electronicSignatureEnabled === true);
const userSignature = ref(null);
const initialUserSignature = ref(null);
// Stroke geometry for the same signature. Kept as its own ref rather than derived from the image
// because it is null whenever the pad can't capture faithfully (a stored signature loaded as
// pixels), and that distinction is exactly what decides raster vs vector rendering on the fax.
const userSignatureStrokes = ref(null);
const initialUserSignatureStrokes = ref(null);
const isDenied = computed(() => orderInfo.value?.status === "Denied");
const showAppeal = computed(() => isDenied.value && appealLettersEnabled.value);
const appealRoute = useRoute();

watch(
  showAppeal,
  (show) => {
    if (!show) return;
    if (!expandedSections.value.includes("appeal")) expandedSections.value.push("appeal");
    if (appealRoute.query.section === "appeal") {
      nextTick(() =>
        document.getElementById("draft-appeal-section")?.scrollIntoView({ behavior: "smooth", block: "start" })
      );
    }
  },
  { immediate: true }
);

// The order's own company config, not the viewer's: the backend submits with order.owning_company's config,
// and Lamar staff review orders for every company. Falls back to the viewer's config when there is no
// order yet or the owner's config is refused (only admin-company users may read another company's).
// Send to Plan awaits clientConfigLoaded, so every (re)load replaces it.
function loadClientConfig(owningCompanyId) {
  // A company-less provider login (LAM-5263) has no client config; skip the fetch (it 400s).
  if (useAuthStore().userInfo?.isProvider) return clientConfigLoaded;
  clientConfigLoaded = (async () => {
    if (owningCompanyId) {
      try {
        clientConfig.value = await mainServices.getClientConfig(owningCompanyId);
        return;
      } catch {
        // Fall through to the viewer's config
      }
    }
    try {
      clientConfig.value = await mainServices.getCurrentUserClientConfig();
    } catch {
      // Suppress - Send to Plan reports the missing config if it needs the CMM popup
    }
  })();
  return clientConfigLoaded;
}

// The DOA attestation decides the Smart Submit pop-up answer, so open its panel once the client config says it applies.
watch(isSmartSubmit, (smart) => {
  if (smart && !expandedSections.value.includes("submission-options"))
    expandedSections.value.push("submission-options");
});

watch(
  () => orderInfo.value?.owningCompanyId,
  (owningCompanyId, previous) => {
    if (owningCompanyId && owningCompanyId !== previous) loadClientConfig(owningCompanyId);
  }
);

onMounted(async () => {
  orderInfo.value.formStep = "Review";
  supplementalDocs.value.push({ label: null, links: [] });
  setDefaultExpandedSections();
  if (props.workspace) openQuestionSections();
  await loadClientConfig(orderInfo.value?.owningCompanyId);
  if (electronicSignatureEnabled.value) {
    try {
      const { signature, signatureStrokes } = await authService.getSignature();
      userSignature.value = signature;
      initialUserSignature.value = signature;
      userSignatureStrokes.value = signatureStrokes ?? null;
      initialUserSignatureStrokes.value = userSignatureStrokes.value;
    } catch {
      // Suppress - signature pad just starts blank if it fails to load
    }
  }
});

onUnmounted(() => {
  expandedSections.value = [];
  clearOrderInfoStorage();
});

function updateOrderInfoField(field, value) {
  orderInfo.value[field] = value;
  trackChange(field, value);
}

// Every v-expansion-panel value in this template. Values for panels that aren't rendered are
// harmless to include, so keep this exhaustive — a missing one silently leaves that panel collapsed
// for read-only viewers (that's how portal-forms was missed).
const ALL_EXPANDABLE_SECTIONS = [
  "appeal",
  "portal-forms",
  "patient-info",
  "provider-info",
  "medical-info",
  "diagnosis-info",
  "patient-notification",
  "additional-questions",
  "insurance-coverage",
  "editable-pdf",
  "supporting-docs",
  "submission-options",
  "signature",
];

function setDefaultExpandedSections() {
  // Read-only viewers (provider logins, LAM-5263) can't act on the page, so nothing is gained by
  // making them open panels by hand — and terminal statuses (Approved/Denied) pre-expand nothing.
  if (!props.actionsVisible) {
    expandedSections.value = [...ALL_EXPANDABLE_SECTIONS];
    return;
  }
  if (incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value = [
      "patient-info",
      "provider-info",
      "medical-info",
      "diagnosis-info",
      "patient-notification",
      "supporting-docs",
      "submission-options",
      "signature",
    ];

    if (orderInfo.value.basicPlusChecklist.length > 0) {
      expandedSections.value.push("additional-questions");
    }
    if (orderInfo.value.payerQuestions.length > 0 && !orderInfo.value.editableSubmissionForm) {
      expandedSections.value.push("insurance-coverage");
    }
    if (orderInfo.value.editableSubmissionForm) {
      expandedSections.value.push("editable-pdf");
    }
  }

  if (reviewStatuses.includes(orderInfo.value.status)) {
    expandedSections.value = ["insurance-coverage", "editable-pdf", "signature"];
  }
}

async function handleSavePartialOrderInfo() {
  if (shouldOfferStartAutocomplete.value) {
    showStartAutocompleteDialog.value = true;
    return false;
  }
  return saveDraftNow();
}

// Fold edits made on the PDF overlay into the form fields, so a save carries them.
async function mergePdfOverlayEdits() {
  if (!orderInfo.value.editableSubmissionForm) return;
  if (!expandedSections.value.includes("editable-pdf")) {
    expandedSections.value.push("editable-pdf");
  }
  if (!editablePdf.value) {
    // timeout to ensure the component is mounted before validation
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  if (editablePdf.value) {
    editablePdf.value.mergeOverlayToPdfForm();
  }
}

async function saveDraftNow({ startAutocomplete = false, rethrow = false } = {}) {
  // A Regenerate in flight saves the draft itself, and a save racing it would be refused or undo the run (LAM-5655).
  // Wait for it; save only when it failed, since its draft save was rolled back with it.
  if (regenerationRequest && (await regenerationRequest).length === 0) return true;
  draftSavingInProgress.value = true;
  await mergePdfOverlayEdits();
  try {
    errors.value = [];
    // Before the order, and not conditional on it: the fax editor's Preview and Send both route
    // here (via onPreSend) and neither submits the order, so this is the only point at which a
    // re-drawn signature reaches the server ahead of the fax being built. A failure here leaves its
    // own message in `errors` and still lets the draft save, so the user's other edits aren't lost
    // to a signature problem.
    await persistSignatureIfChanged();
    await savePartialOrderInfo({ startAutocomplete });
  } catch (error) {
    errors.value = ["Failed to save draft. Please try again."];
    draftSavingInProgress.value = false;
    if (rethrow) throw error;
    return false;
  }
  draftSavingInProgress.value = false;
  emit("draftSaved");
  return true;
}

function hasUnmergedPdfEdits() {
  const objects = orderInfo.value?.editableSubmissionForm?.objects;
  const fields = editablePdf.value?.getFormData?.();
  if (!objects || !fields) return false;
  return fields.some((field) => objects.find((obj) => obj.id === field.id)?.text !== field.value);
}

const showStartAutocompleteDialog = ref(false);
const autocompleteStartRequested = ref(false);

const supportingDocsCount = computed(() => {
  const chartDocs = (orderInfo.value?.patientChart || []).filter((doc) => doc?.links?.length > 0).length;
  const pendingDocs = (supplementalDocs.value || []).filter((doc) => doc?.links?.length > 0).length;
  return chartDocs + pendingDocs;
});

const shouldOfferStartAutocomplete = computed(
  () => orderInfo.value?.status === "Incomplete" && supportingDocsCount.value > 0 && !autocompleteStartRequested.value
);

const startAutocompleteMessage = computed(() => {
  const count = supportingDocsCount.value;
  const docs = count === 1 ? "1 supporting document" : `${count} supporting documents`;
  return `This submission is incomplete. Start autocomplete to draft answers to its clinical questions from your ${docs}?`;
});

async function onStartAutocompleteConfirm() {
  try {
    await saveDraftNow({ startAutocomplete: true });
    if (errors.value.length === 0) {
      autocompleteStartRequested.value = true;
    }
  } finally {
    showStartAutocompleteDialog.value = false;
  }
}

async function onStartAutocompleteCancel() {
  await saveDraftNow();
}

// Open the Signature panel from outside this component. Needed because the send-fax flow lives in the
// parent page: when the backend refuses a fax for an un-re-drawn signature, the pad the user has to
// act on is in here, inside a lazily-rendered expansion panel that may well be collapsed.
function focusSignatureSection() {
  if (!expandedSections.value.includes("signature")) {
    expandedSections.value.push("signature");
  }
}

function openQuestionSections() {
  for (const section of ["insurance-coverage", "additional-questions"]) {
    if (!expandedSections.value.includes(section)) expandedSections.value.push(section);
  }
}

function revealQuestion(id) {
  openQuestionSections();
  return nextTick(() => formRoot.value?.$el.querySelector(`[data-attention-id="${CSS.escape(id)}"]`));
}

const actionState = computed(() => ({
  submitLabel: buttonText.value,
  submitDisabled: submitButtonDisabled.value,
  submitDisabledReason: isSendToPlanDisabledByDelegateAgreement.value ? sendToPlanDisabledReason : null,
  prescriberDisabled: isSendToPrescriberDisabled.value,
  prescriberDisabledReason: prescriberDisabledReason.value,
  prescriberSending: prescriberSending.value || props.prescriberOpening,
  saving: draftSavingInProgress.value,
  saveable: !props.disabled,
  archivable: props.archivable && Boolean(orderInfo.value?.orderKey),
  archived: isArchived.value,
  archiveDisabledReason: regenerationBusy.value ? REGENERATION_BUSY_REASON : null,
  regenerateVisible: canRegenerateAnswers.value,
  regenerating: regeneratingAnswers.value,
}));

defineExpose({
  declaredPayerQuestionIds,
  saveDraft: saveDraftNow,
  performSubmission,
  focusSignatureSection,
  submit: submitOrderInfo,
  sendToPrescriber,
  regenerateAnswers,
  stopRegeneratingAnswers,
  requestSaveDraft: handleSavePartialOrderInfo,
  revealQuestion,
  hasUnmergedPdfEdits,
  actionState,
  errors,
});

async function validateForm() {
  formButtonClicked.value = true;
  if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
    mergeSupplementalDocsWithPatientChart();
  }
  errors.value = [];
  if (!validPatientInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("patient-info");
    errors.value.push("Please fill in all required patient information fields.");
  }
  if (!validProviderInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("provider-info");
    errors.value.push("Please fill in all required provider information fields.");
  }
  if (!validMedicationInfo() && incompleteStatuses.includes(orderInfo.value.status)) {
    expandedSections.value.push("medical-info");
    errors.value.push("Please fill in all required medication information fields.");
  }
  // The patientChartNotNeededOptIn clause reaches statuses where the diagnosis section is read-only
  // (the opt-in is tickable whenever supporting docs are editable), so gate it — otherwise submit is
  // blocked by a diagnosis the user has no field to enter.
  if (
    orderFieldsEditable.value &&
    !validDiagnosisInfo() &&
    (incompleteStatuses.includes(orderInfo.value.status) || orderInfo.value.patientChartNotNeededOptIn)
  ) {
    expandedSections.value.push("diagnosis-info");
    errors.value.push("Please fill in all required diagnosis information fields.");
  }
  if (supportingDocsEditable.value && !validSupportingDocs(!orderInfo.value.patientChartNotNeededOptIn)) {
    expandedSections.value.push("supporting-docs");
    errors.value.push("Please add at least one supporting document.");
  }
  if (orderInfo.value.editableSubmissionForm) {
    if (!expandedSections.value.includes("editable-pdf")) {
      expandedSections.value.push("editable-pdf");
    }
    if (!editablePdf.value) {
      // timeout to ensure the component is mounted before validation
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
    if (editablePdf.value) {
      editablePdf.value.mergeOverlayToPdfForm();
      const isValid = editablePdf.value.validateAll();
      if (!isValid) {
        errors.value.push("Please fill in all required fields in the PDF form.");
      }
    }
  }
  expandedSections.value.push("insurance-coverage");
  expandedSections.value.push("additional-questions");
  await new Promise((resolve) => setTimeout(resolve, 300));
  const errorClassNames = ["v-input--error", "form-validation-alert"];
  let missingInput = [];
  errorClassNames.forEach((className) => {
    const foundElements = document.getElementsByClassName(className);
    missingInput = [...missingInput, ...foundElements];
  });
  // A question the page hides stays mounted under d-none; the user can't see or fix it (LAM-6069).
  missingInput = missingInput.filter((el) => !el.closest(".d-none"));
  if (missingInput.length === 0 && errors.value.length === 0) {
    return true;
  } else {
    await nextTick();
    requestAnimationFrame(() => {
      if (missingInput.length > 0) {
        missingInput[0].scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        const firstSection = expandedSections.value[0];
        const sectionEl = firstSection && document.querySelector(`[data-section="${firstSection}"]`);
        sectionEl?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    return false;
  }
}

async function submitOrderInfo() {
  if (isSendToPlanDisabledByDelegateAgreement.value) {
    return;
  }
  if (await maybePromptDelegateAgreementConfirmation()) {
    return;
  }
  await continueSubmitAfterDelegateChecks();
}

function onCmmPopupConfirm(answers) {
  if (isSmartSubmit.value) {
    doaAttested.value = answers[DOA_POPUP_QUESTION_ID] === "Yes";
  }
  orderInfo.value.cmmPopupAnswers = answers;
  performSubmission();
}

async function performSubmission() {
  draftSavingInProgress.value = true;
  try {
    if (isReopening.value) {
      if (!isReopenEligible.value) {
        draftSavingInProgress.value = false;
        return;
      }
      // Include field change tracking data for backend processing
      const submissionData = {
        ...orderInfo.value,
        requiresNewSubmission: requiresNewSubmission.value,
      };
      await orderInfoService.reopenSubmission(orderInfo.value.orderKey, submissionData, declaredPayerQuestionIds.value);
      errors.value = [];
      finishSubmission({ reopened: true });
    } else {
      orderInfo.value.formStep = "Send to Plan";
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }
      if (orderInfo.value.editableSubmissionForm) {
        if (!expandedSections.value.includes("editable-pdf")) {
          expandedSections.value.push("editable-pdf");
        }
        if (!editablePdf.value) {
          // timeout to ensure the component is mounted before validation
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        if (editablePdf.value) {
          editablePdf.value.mergeOverlayToPdfForm();
        }
      }
      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value, declaredPayerQuestionIds.value);
      errors.value = [];
      finishSubmission({ reopened: false });
    }
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  draftSavingInProgress.value = false;
}

function finishSubmission(result) {
  if (!props.navigateAfterSubmit) {
    emit("submitted", result);
    return;
  }
  expandedSections.value = [];
  clearOrderInfoStorage();
  router.push(listReturnTarget({ name: "all-submissions" }));
  window.scrollTo(0, 0);
}

async function changeFormSelection(selectedFormId) {
  await new Promise((resolve) => setTimeout(resolve, 0));
  preloader.value = true;
  try {
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    let data = orderInfo.value;
    data.portalFormId = selectedFormId;
    await orderInfoService.changeFormSelection(orderInfo.value.orderKey, data);
    errors.value = [];
    expandedSections.value = [];
    clearOrderInfoStorage();
    router.push(listReturnTarget({ name: "all-submissions" }));
    window.scrollTo(0, 0);
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

async function changeFormulary() {
  await new Promise((resolve) => setTimeout(resolve, 0));
  preloader.value = true;
  try {
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    await orderInfoService.changeOrderFormulary(orderInfo.value.orderKey, orderInfo.value);
    errors.value = [];
    expandedSections.value = [];
    clearOrderInfoStorage();
    router.push(listReturnTarget({ name: "all-submissions" }));
    window.scrollTo(0, 0);
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  }
  preloader.value = false;
}

// Clinical questions are only drafted once the PA is sent to processing, so a prescriber sent the PA
// before that gets blank questions (LAM-5843). Warn rather than block: an early send stays possible.
const showProcessingFirstDialog = ref(false);
const prescriberSending = ref(false);
const shouldSendToProcessingFirst = computed(
  () => buttonText.value === "Send to Processing" && !submitButtonDisabled.value
);

async function sendToPrescriber() {
  if (isSendToPrescriberDisabled.value) {
    return;
  }
  if (shouldSendToProcessingFirst.value) {
    showProcessingFirstDialog.value = true;
    return;
  }
  await proceedSendToPrescriber();
}

function onProcessingFirstConfirm() {
  showProcessingFirstDialog.value = false;
  if (props.workspace) {
    emit("sendToPlanRequested");
  } else {
    submitOrderInfo();
  }
}

async function proceedSendToPrescriber() {
  if (checklistHasValidationErrors.value) {
    errors.value = ["Please fix all answer format errors before sending to prescriber."];
    if (!expandedSections.value.includes("insurance-coverage")) expandedSections.value.push("insurance-coverage");
    if (!expandedSections.value.includes("additional-questions")) expandedSections.value.push("additional-questions");
    await new Promise((resolve) => setTimeout(resolve, 300));
    await nextTick();
    requestAnimationFrame(() => {
      const errorEl = document.querySelector(".v-input--error");
      if (errorEl) errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return;
  }
  if (!orderInfo.value.orderKey) {
    // No order yet — nothing to persist; let the parent's handler take over.
    emit("sendToPrescriber");
    return;
  }
  draftSavingInProgress.value = true;
  prescriberSending.value = true;
  try {
    errors.value = [];
    // Fold any newly-added free-form supporting docs into patientChart before saving,
    // matching what validateForm() does for the Send-to-Plan path.
    if (supplementalDocs.value.length > 0 && supplementalDocs.value[0].links.length > 0) {
      mergeSupplementalDocsWithPatientChart();
    }
    // Persist the current in-memory orderInfo (including the cleared/changed payer-question
    // answers, which travel as the declared answer pairs) so the backend SYNC_ANSWERS_TO_CMM
    // pipeline reads fresh data. form_step="Review" hits PartialOrderSaveView's REVIEW branch which does a
    // flat _save_full_form (no status transition, no pipeline kick-off), unlike
    // "Send to Plan". See performSubmission() for the sibling pattern.
    orderInfo.value.formStep = "Review";
    await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value, declaredPayerQuestionIds.value);
    emit("sendToPrescriber");
  } catch (err) {
    errors.value = tryParseApiErrors(err);
  } finally {
    draftSavingInProgress.value = false;
    prescriberSending.value = false;
  }
}

// The Regenerate POST in flight, resolving to its errors; saveDraftNow waits on it.
let regenerationRequest = null;

// Errors of a Regenerate or Stop go under the form on the classic page; the workspace shows them at the top of its
// pane instead, so they are returned rather than shown twice.
function showRegenerationErrors(failure) {
  if (!props.workspace) errors.value = failure;
  return failure;
}

// One request saves the draft (so newly attached documents are read) and starts the run; the page then shows
// Processing until the answers are merged (LAM-5655). Resolves to the errors, [] when the run started.
function regenerateAnswers() {
  if (!canRegenerateAnswers.value || draftSavingInProgress.value) return Promise.resolve([]);
  regenerationRequest = startRegeneration().finally(() => (regenerationRequest = null));
  return regenerationRequest;
}

async function startRegeneration() {
  draftSavingInProgress.value = true;
  regeneratingAnswers.value = true;
  errors.value = [];
  try {
    // Prepared as saveDraftNow prepares a draft: the reload after the start would drop anything left on screen.
    await mergePdfOverlayEdits();
    if (!(await persistSignatureIfChanged())) {
      // Starting would reload the page over the redrawn signature: keep it, with the reason it was not saved.
      const failure = errors.value;
      errors.value = [];
      return showRegenerationErrors(failure);
    }
    // Any filled row, not just the first: a document uploaded into row 2 while row 1 is empty must be read too.
    if (supplementalDocs.value.some((doc) => doc.links?.length > 0)) {
      mergeSupplementalDocsWithPatientChart();
    }
    dropUnansweredChecklistRows();
    orderInfo.value.formStep = "Review";
    const run = await orderInfoService.regenerateAnswers(
      orderInfo.value.orderKey,
      orderInfo.value,
      declaredPayerQuestionIds.value
    );
    // Track the run from the 202 itself, so a failed follow-up reload still leaves the page polling it.
    orderInfo.value.status = run?.status ?? PA_STATUS_VALUE_BY_KEY.PROCESSING;
    if (orderInfo.value.orderSubmission)
      orderInfo.value.orderSubmission.pipelineStep = REGENERATE_ANSWERS_PIPELINE_STEP;
    orderInfo.value.answerRegenerationStartedOn = run?.startedOn ?? null;
    emit("answersRegenerationChanged");
    return [];
  } catch (err) {
    return showRegenerationErrors(tryParseApiErrors(err));
  } finally {
    draftSavingInProgress.value = false;
    regeneratingAnswers.value = false;
  }
}

// Resolves to the errors, [] when the run stopped or had already finished.
async function stopRegeneratingAnswers() {
  if (stoppingRegeneration.value) return [];
  stoppingRegeneration.value = true;
  errors.value = [];
  try {
    await orderInfoService.stopRegeneratingAnswers(orderInfo.value.orderKey);
    // Reload only on success: the reload remounts this form and would wipe the error before anyone reads it.
    emit("answersRegenerationChanged");
    return [];
  } catch (err) {
    // 409: the run finished before Stop reached it, so show the result rather than an error.
    if (err?.response?.status === 409) {
      emit("answersRegenerationChanged");
      return [];
    }
    return showRegenerationErrors(tryParseApiErrors(err));
  } finally {
    stoppingRegeneration.value = false;
  }
}
</script>

<style lang="scss" scoped>
.ws-panels.v-expansion-panels {
  gap: 0;
  border: 1px solid $lh_dash_border;
  border-radius: 14px;
  background: $lh_dash_surface;
  box-shadow: $lh_shadow_md;

  > .v-expansion-panel {
    margin: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;

    &::after {
      display: none;
    }

    &:hover {
      box-shadow: none !important;
    }

    & + .v-expansion-panel {
      border-top: 1px solid rgba($lh_color_navy, 0.06) !important;
    }
  }

  > .v-expansion-panel > .v-expansion-panel-title {
    position: sticky;
    top: -14px;
    z-index: 3;
    justify-content: flex-start;
    gap: 14px;
    min-height: 58px;
    padding: 0 16px 0 14px;
    background: $lh_dash_surface !important;
    font-size: 14.5px;
    font-weight: 600;
    letter-spacing: -0.005em;
    color: $lh_color_navy_dark !important;
    transition: background-color 160ms ease;

    > .v-icon {
      flex: none;
      width: 32px;
      height: 32px;
      margin-right: 0 !important;
      border-radius: 9px;
      font-size: 18px;
      background: rgba($lh_color_navy, 0.05);
      color: $lh_color_blue_gray !important;
      transition: background-color 200ms ease, color 200ms ease;
    }

    :deep(.v-expansion-panel-title__icon) {
      color: $lh_color_blue_gray;

      .v-icon {
        transition: transform 220ms cubic-bezier(0.2, 0, 0, 1);
      }
    }

    :deep(.v-expansion-panel-title__overlay) {
      opacity: 0 !important;
    }

    &:hover {
      background: mix($lh_color_navy, $lh_dash_surface, 3%) !important;
    }
  }

  > .v-expansion-panel:first-child > .v-expansion-panel-title {
    border-radius: 14px 14px 0 0;
  }

  > .v-expansion-panel:last-child:not(.v-expansion-panel--active) > .v-expansion-panel-title {
    border-radius: 0 0 14px 14px;
  }

  > .v-expansion-panel--active > .v-expansion-panel-title {
    box-shadow: 0 1px 0 rgba($lh_color_navy, 0.06);

    > .v-icon {
      background: rgba($lh_color_blue, 0.1);
      color: $lh_color_blue !important;
    }

    :deep(.v-expansion-panel-title__icon .v-icon) {
      transform: rotate(180deg);
    }

    .ws-panel-summary {
      display: none;
    }
  }

  :deep(.v-expansion-panel__shadow) {
    display: none;
  }

  :deep(.v-expansion-panel-text__wrapper) {
    padding: 20px 24px 24px;
  }

  :deep(.section-container),
  :deep(.patient-information) {
    margin-bottom: 8px !important;
  }

  :deep(.form-validation-alert) {
    margin-bottom: 8px !important;
  }

  :deep(.v-expansion-panel-text__wrapper .v-btn.v-btn--size-large),
  :deep(.v-expansion-panel-text__wrapper .v-btn.v-btn--size-default) {
    height: 36px !important;
    padding: 0 14px !important;
    font-size: 13.5px !important;
    letter-spacing: 0 !important;
    text-transform: none !important;
  }

  :deep(.v-expansion-panel-text__wrapper .v-col.text-body-3-1.weight-600.text-primary) {
    padding-left: 24px;
  }

  :deep(.v-expansion-panel-text__wrapper .v-col.text-body-3-1.weight-600.text-primary),
  :deep(.v-expansion-panel-text__wrapper h3.text-primary.weight-600) {
    margin-bottom: 4px;
    font-family: $lh_font_mono;
    font-size: 10.5px;
    font-weight: 500 !important;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $lh_color_blue_gray !important;
  }

  :deep(.patient-information-container > .v-col > .patient-information > .v-col > h3.text-primary),
  :deep(.v-expansion-panel-text__wrapper > .section-container > .v-col > h3.text-primary) {
    display: none;
  }
}

.ws-panel-summary {
  flex: 1 1 0;
  min-width: 0;
  padding: 0 8px 0 12px;
  overflow: hidden;
  text-align: right;
  font-size: 12.5px;
  font-weight: 400;
  color: $lh_color_blue_gray;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
